import { ObjectId } from 'bson';
import { computed, makeObservable } from 'mobx';
import type { Moment } from 'moment';

import { concatPath, moment } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel } from '@feathr/rachis';

export type TSegmentExportState =
  | 'pending'
  | 'exporting'
  | 'stopping'
  | 'ready'
  | 'failed'
  | 'stopped'
  | 'expired';

export type TSegmentExportType = 'email_activity' | 'persons' | 'breadcrumb';

export type TSegmentExportPhase = 'enumerating' | 'exporting';

/* Mirrors anhinga's `ACTIVE_EXPORT_STATES`. Excludes `stopping`: the BE 409s a second stop. */
const ACTIVE_STATES = new Set<TSegmentExportState>(['pending', 'exporting']);

export interface ISegmentExport extends IBaseAttributes {
  person_segment: string;
  account: string;
  export_type: TSegmentExportType;
  state: TSegmentExportState;
  phase: TSegmentExportPhase | null;
  params: Record<string, unknown>;
  email: string;
  name?: string;
  created_by: string;
  is_archived: boolean;
  date_created: string | null;
  date_started: string | null;
  date_finished: string | null;
  rows_written: number;
  files_written: number;
  file_size: number | null;
  current_chunk_index: number;
  total_chunks: number | null;
  /** Percent complete, 0-100, as reported by the BE. Null until the export starts. */
  progress: number | null;
  error: string | null;
}

export class SegmentExport extends DisplayModel<ISegmentExport> {
  public readonly className = 'SegmentExport';

  public get constraints(): TConstraints<ISegmentExport> {
    return {
      email: {
        presence: { allowEmpty: false },
      },
      person_segment: {
        presence: { allowEmpty: false },
      },
      export_type: {
        presence: { allowEmpty: false },
        inclusion: ['email_activity', 'persons', 'breadcrumb'],
      },
    };
  }

  constructor(attributes: Partial<ISegmentExport> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public override getDefaults(): Partial<ISegmentExport> {
    return {
      is_archived: false,
      rows_written: 0,
      files_written: 0,
      current_chunk_index: 0,
    };
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/data/exports/${this.id}`, pathSuffix);
  }

  @computed
  public get name(): string {
    return this.get('name') ?? '';
  }

  @computed
  public get dateCreated(): Moment {
    const dateCreated = this.get('date_created');
    if (dateCreated) {
      return moment.utc(dateCreated);
    }
    return moment.utc(new ObjectId(this.id).getTimestamp());
  }

  @computed
  public get dateFinished(): Moment | null {
    const dateFinished = this.get('date_finished');
    return dateFinished ? moment.utc(dateFinished) : null;
  }

  @computed
  public get downloadUrl(): string {
    if (this.collection) {
      return `${this.collection.url()}${this.id}/download`;
    }
    return '';
  }

  /** Whether a Stop action applies — true only while the export is in progress. */
  @computed
  public get canStop(): boolean {
    return ACTIVE_STATES.has(this.get('state'));
  }

  /** Whether a stop is outstanding and the running job has yet to observe it. */
  @computed
  public get isStopping(): boolean {
    return this.get('state') === 'stopping';
  }

  /** Whether the export has finished writing files and can be downloaded. */
  @computed
  public get canDownload(): boolean {
    return this.get('state') === 'ready';
  }

  /**
   * Whether the export can be archived. `is_archived` is delete-equivalent and
   * orthogonal to `state`, so any not-yet-archived export is archivable
   * regardless of where it sits in the state machine.
   */
  @computed
  public get canArchive(): boolean {
    return !this.get('is_archived');
  }

  /**
   * Whether a previously-ready export's download link has aged out. BE-emitted
   * (`state` flips to `expired`); the UI shows a disabled Download with an
   * explanatory tooltip.
   */
  @computed
  public get isExpired(): boolean {
    return this.get('state') === 'expired';
  }
}

export class SegmentExports extends Collection<SegmentExport> {
  public getClassName(): string {
    return 'segment-exports';
  }

  public getModel(attributes: Partial<ISegmentExport>): SegmentExport {
    return new SegmentExport(attributes);
  }

  public override isActiveForRefresh(model: SegmentExport): boolean {
    return ACTIVE_STATES.has(model.get('state'));
  }
}
